import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import whatsappMicroserviceService from '../services/whatsappMicroserviceService';

const LS_KEY  = 'whatsapp_incoming_last_seen';
const POLL_MS = 30000;

// Si no hay registro previo, busca mensajes de los últimos 5 minutos para
// no perder respuestas recientes al abrir la aplicación.
const getInitialLastSeen = () => {
  const stored = localStorage.getItem(LS_KEY);
  if (stored) return stored;
  return new Date(Date.now() - 5 * 60 * 1000).toISOString();
};

function WhatsAppIncomingMonitor() {
  const navigate    = useNavigate();
  const lastSeenRef = useRef(getInitialLastSeen());

  useEffect(() => {
    let cancelled = false;

    const check = async () => {
      try {
        const health = await whatsappMicroserviceService.getHealth();
        const connected =
          health?.connected === true ||
          health?.whatsapp?.connected === true ||
          health?.whatsapp?.status === 'connected';
        if (!connected) return;

        const logs = await whatsappMicroserviceService.getLogs(100);
        if (!Array.isArray(logs)) return;

        const lastSeen    = lastSeenRef.current;
        const newIncoming = logs.filter(
          (log) => log.direction === 'incoming' && log.timestamp > lastSeen
        );

        if (newIncoming.length === 0) return;

        // Avanzar el cursor al mensaje más nuevo
        const newestTs = newIncoming.reduce(
          (max, log) => (log.timestamp > max ? log.timestamp : max),
          newIncoming[0].timestamp
        );
        lastSeenRef.current = newestTs;
        localStorage.setItem(LS_KEY, newestTs);

        if (cancelled) return;

        if (newIncoming.length === 1) {
          const log     = newIncoming[0];
          const preview = log.message?.length > 70
            ? log.message.slice(0, 70) + '…'
            : log.message;

          toast(
            ({ closeToast }) => (
              <div
                onClick={() => { navigate('/admin/whatsapp'); closeToast(); }}
                style={{ cursor: 'pointer' }}
              >
                <strong style={{ color: '#128c7e' }}>📲 WhatsApp — respuesta recibida</strong>
                <div style={{ fontSize: '0.88rem', margin: '4px 0 2px' }}>
                  <strong>{log.phone}</strong>: {preview}
                </div>
                <small style={{ color: '#888' }}>Clic para abrir el panel</small>
              </div>
            ),
            { autoClose: 8000, icon: false }
          );
        } else {
          toast(
            ({ closeToast }) => (
              <div
                onClick={() => { navigate('/admin/whatsapp'); closeToast(); }}
                style={{ cursor: 'pointer' }}
              >
                <strong style={{ color: '#128c7e' }}>📲 WhatsApp — {newIncoming.length} respuestas nuevas</strong>
                <div style={{ fontSize: '0.88rem', margin: '4px 0 2px' }}>
                  {newIncoming.map((log) => (
                    <div key={log.id ?? log.timestamp}>
                      <strong>{log.phone}</strong>: {log.message?.slice(0, 40)}{log.message?.length > 40 ? '…' : ''}
                    </div>
                  ))}
                </div>
                <small style={{ color: '#888' }}>Clic para abrir el panel</small>
              </div>
            ),
            { autoClose: 10000, icon: false }
          );
        }
      } catch {
        // El servicio no está disponible o no conectado — ignorar silenciosamente.
      }
    };

    check();
    const interval = setInterval(check, POLL_MS);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [navigate]);

  return null;
}

export default WhatsAppIncomingMonitor;
